import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { AiReviewCard } from 'src/components/document-review/review/contractor/AiReviewCard'
import { BusinessEntityCard } from 'src/components/document-review/review/contractor/BusinessEntityCard'
import { ContractorDecisionSection } from 'src/components/document-review/review/contractor/ContractorDecisionSection'
import { ContractorReviewForm } from 'src/components/document-review/review/contractor/ContractorReviewForm'
import { McLiteCard } from 'src/components/document-review/review/contractor/McLiteCard'
import { PartnerInstructionsCard } from 'src/components/document-review/review/contractor/PartnerInstructionsCard'
import { DocumentNavigator } from 'src/components/document-review/review/DocumentNavigator'
import { ReviewHeader } from 'src/components/document-review/review/ReviewHeader'
import { FileViewer } from 'src/components/shared/file-viewer/FileViewer'
import { Button } from 'src/components/shared/Button'
import {
  AdminFetchDocumentReviewStep,
  AdminFetchDocumentReviewSteps,
  AdminFetchReviewRejectionReasons,
} from 'src/constants/actionTypes'
import { CollectDataStepType } from 'src/constants/collect-data/enums'
import { ACCEPTED_DOCUMENT_EXTENSIONS } from 'src/constants/document-review/constants'
import { DocumentReviewTab } from 'src/constants/document-review/enums'
import { ADMIN_ROUTES } from 'src/constants/routes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { getInternalIdentity } from 'src/selectors/internalAuth'
import {
  getDocumentReviewStep,
  getDocumentReviewStepError,
  getDocumentReviewStepLoading,
  getDocumentReviewSteps,
  getDocumentReviewSubmitting,
  getReviewRejectionReasons,
} from 'src/selectors/documentReview'
import { resolveStepTypeLabel } from 'src/utils/documentReviewList'

const QUEUE_URL = `${ADMIN_ROUTES.INTERNAL_DOCUMENT_REVIEW_URL}?tab=${DocumentReviewTab.Contractor}`

const ContractorReviewPageComponent = () => {
  const { stepId } = useParams<{ stepId: string }>()
  const navigate = useNavigate()

  const [documentIndex, setDocumentIndex] = useState<number>(0)

  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const step = useSelector(getDocumentReviewStep)
  const isLoading = useSelector(getDocumentReviewStepLoading)
  const error = useSelector(getDocumentReviewStepError)
  const queue = useSelector(getDocumentReviewSteps)
  const rejectionReasons = useSelector(getReviewRejectionReasons)
  const isSubmitting = useSelector(getDocumentReviewSubmitting)
  // `useAuthSession` only answers whether a session exists — the signed-in identity itself
  // (WorkOS AuthKit + /v1/internal/auth/me) lives in the internalAuth slice.
  const identity = useSelector(getInternalIdentity)
  const reviewedByUser = identity?.email ?? ''

  useEffect(() => {
    if (!isAuthenticated || !stepId) {
      return
    }

    // Reset to the first file whenever the step changes, so advancing to the next review does not
    // land on document 3 of a step that has one.
    setDocumentIndex(0)
    asyncDispatchWithErrorToast(AdminFetchDocumentReviewStep.request({ stepId }))
  }, [asyncDispatchWithErrorToast, isAuthenticated, stepId])

  useEffect(() => {
    if (!isAuthenticated || rejectionReasons.length > 0) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchReviewRejectionReasons.request())
  }, [asyncDispatchWithErrorToast, isAuthenticated, rejectionReasons.length])

  // The header's "n of total" position and `goToNextInQueue` below both read `queue`
  // (`getDocumentReviewSteps`), which only `DocumentReviewQueuePage` ever populates. A deep link
  // or a refresh on this screen would otherwise leave the queue empty and strand every decision
  // back on the queue page instead of advancing — mirrors the equivalent fetch on `McReviewPage`.
  useEffect(() => {
    if (!isAuthenticated || queue.length > 0) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchDocumentReviewSteps.request())
  }, [asyncDispatchWithErrorToast, isAuthenticated, queue.length])

  const handleBack = useCallback(() => {
    navigate(QUEUE_URL)
  }, [navigate])

  const goToNextInQueue = useCallback(() => {
    const currentIndex = queue.findIndex((row) => {
      return row.id === stepId
    })
    // The decided step is about to leave the queue on refetch, so "next" is the row that follows
    // it in the list we have now.
    const nextRow = queue[currentIndex + 1] ?? queue[0]

    if (!nextRow || nextRow.id === stepId) {
      navigate(QUEUE_URL)

      return
    }

    navigate(ADMIN_ROUTES.INTERNAL_DOCUMENT_REVIEW_CONTRACTOR_URL.replace(':stepId', nextRow.id))
  }, [navigate, queue, stepId])

  const goToQueueOffset = useCallback(
    (offset: number) => {
      const currentIndex = queue.findIndex((row) => {
        return row.id === stepId
      })
      const targetRow = currentIndex === -1 ? undefined : queue[currentIndex + offset]

      if (!targetRow) {
        return
      }

      navigate(
        ADMIN_ROUTES.INTERNAL_DOCUMENT_REVIEW_CONTRACTOR_URL.replace(':stepId', targetRow.id),
      )
    },
    [navigate, queue, stepId],
  )

  const handlePreviousInQueue = useCallback(() => {
    goToQueueOffset(-1)
  }, [goToQueueOffset])

  const handleNextInQueue = useCallback(() => {
    goToQueueOffset(1)
  }, [goToQueueOffset])

  const handleDecided = useCallback(() => {
    // Fired and not awaited: `goToNextInQueue` deliberately reads the queue as it stands right
    // now (see its own comment) — waiting for this refetch would mean navigating with a queue
    // that already dropped the just-decided step.
    asyncDispatchWithErrorToast(AdminFetchDocumentReviewSteps.request())
    goToNextInQueue()
  }, [asyncDispatchWithErrorToast, goToNextInQueue])

  const handleSkip = useCallback(() => {
    goToNextInQueue()
  }, [goToNextInQueue])

  const handlePreviousDocument = useCallback(() => {
    setDocumentIndex((previousIndex) => {
      return Math.max(0, previousIndex - 1)
    })
  }, [])

  const documents = step?.partnerDriverCollectedData.documents ?? []

  const handleNextDocument = useCallback(() => {
    setDocumentIndex((previousIndex) => {
      return Math.min(documents.length - 1, previousIndex + 1)
    })
  }, [documents.length])

  // Position comes from the loaded queue rather than being passed through navigation state, so it
  // survives a refresh and a pasted link.
  const queuePosition = useMemo(() => {
    const index = queue.findIndex((row) => {
      return row.id === stepId
    })

    return index + 1
  }, [queue, stepId])

  const handleRetry = useCallback(() => {
    if (!stepId) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchDocumentReviewStep.request({ stepId }))
  }, [asyncDispatchWithErrorToast, stepId])

  if (isLoading) {
    return (
      <div css={tw`flex flex-1 items-center justify-center p-6`}>
        <Typography variant="body2" css={tw`text-shades-500`}>
          Loading document…
        </Typography>
      </div>
    )
  }

  if (error || !step) {
    return (
      <div
        data-testid="document-review-load-error"
        css={tw`flex flex-1 flex-col items-center justify-center gap-4 p-6`}
      >
        <Typography variant="h4">This document could not be loaded</Typography>
        <Typography variant="body2" css={tw`text-shades-500`}>
          {error ?? 'It may have been reviewed by someone else.'}
        </Typography>
        <div css={tw`flex flex-row gap-2`}>
          <Button size="small" color="white" onClick={handleRetry}>
            Try again
          </Button>
          <Button size="small" onClick={handleBack}>
            Back to queue
          </Button>
        </div>
      </div>
    )
  }

  const currentDocument = documents[documentIndex]
  const extension = currentDocument?.extension?.toLowerCase() ?? ''
  const isSupported = ACCEPTED_DOCUMENT_EXTENSIONS.includes(extension)
  const { partnerDriver } = step.partnerDriverCollectedData

  const mcLiteLegalName =
    partnerDriver.mcLitePartnerDriverRelations?.[0]?.mcLite?.businessInfo?.legalName ?? ''

  return (
    <div
      data-testid="contractor-review-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col gap-6 overflow-hidden p-6`}
    >
      <ReviewHeader
        title={resolveStepTypeLabel({
          type: step.type,
          disableInsuranceUpload: Boolean(step.metadata?.disableInsuranceUpload),
          insuranceProductName: step.metadata?.insurancePolicyProduct?.insuranceProduct?.name,
        })}
        subtitle={[partnerDriver.firstName, partnerDriver.lastName].filter(Boolean).join(' ')}
        position={queuePosition}
        total={queue.length}
        onBack={handleBack}
        onPrevious={handlePreviousInQueue}
        onNext={handleNextInQueue}
      />

      <div css={tw`grid min-h-0 flex-1 grid-cols-2 gap-6 overflow-hidden`}>
        <div css={tw`flex min-h-0 flex-col gap-3 overflow-hidden`}>
          <DocumentNavigator
            index={documentIndex}
            total={documents.length}
            onPrevious={handlePreviousDocument}
            onNext={handleNextDocument}
          />
          {currentDocument && isSupported ? (
            <FileViewer
              filePath={currentDocument.filePath}
              isPdf={extension === 'pdf'}
              fileName={currentDocument.fileName}
              fileExtension={extension}
            />
          ) : (
            <div
              css={tw`flex flex-1 items-center justify-center rounded-lg border border-solid border-shades-100 bg-shades-0`}
            >
              <Typography variant="body2" css={tw`text-shades-500`}>
                {currentDocument
                  ? 'This file type cannot be previewed.'
                  : 'No document was uploaded.'}
              </Typography>
            </div>
          )}
        </div>

        <div css={tw`flex min-h-0 flex-col gap-4 overflow-y-auto`}>
          <ContractorReviewForm step={step}>
            <Fragment>
              <ContractorDecisionSection
                step={step}
                reasons={rejectionReasons}
                reviewedByUser={reviewedByUser}
                isSubmitting={isSubmitting}
                onDecided={handleDecided}
                onSkip={handleSkip}
              />
              <AiReviewCard aiDocumentReview={step.aiDocumentReview} stepType={step.type} />
              {partnerDriver.partner.gigsafeConfig?.internalDocumentReviewInstructions ? (
                <PartnerInstructionsCard
                  instructions={
                    partnerDriver.partner.gigsafeConfig.internalDocumentReviewInstructions
                  }
                />
              ) : null}
              {step.type === CollectDataStepType.EIN &&
              (step.partnerDriverCollectedData.metadata?.businessEntity?.verifications?.length ??
                0) > 0 ? (
                <BusinessEntityCard
                  verifications={
                    step.partnerDriverCollectedData.metadata?.businessEntity?.verifications ?? []
                  }
                />
              ) : null}
              {mcLiteLegalName ? <McLiteCard legalName={mcLiteLegalName} /> : null}
            </Fragment>
          </ContractorReviewForm>
        </div>
      </div>
    </div>
  )
}

export const ContractorReviewPage = memo(ContractorReviewPageComponent)
